import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from '../Modal';
import {
  ClientMedicalInfoDto,
  CreateClientMedicalInfoDto,
  UpdateClientMedicalInfoDto,
  MedicalConditionStatus,
  medicalConditionsApi,
  handleApiError
} from '../../services/medicalConditionsService';
import { DiseaseDto, diseasesApi } from '../../services/settingsService';

interface MedicalConditionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clientId: number;
  condition?: ClientMedicalInfoDto | null;
}

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
`;

const Select = styled.select<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.hasError ? '#dc2626' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);
  background: var(--surface-color);

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#dc2626' : 'var(--primary-color)'};
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.hasError ? '#dc2626' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#dc2626' : 'var(--primary-color)'};
  }
`;

const TextArea = styled.textarea<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.hasError ? '#dc2626' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);
  min-height: 100px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#dc2626' : 'var(--primary-color)'};
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 8px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: var(--text-primary);

  input[type="radio"] {
    cursor: pointer;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: var(--text-primary);

  input[type="checkbox"] {
    cursor: pointer;
    width: 18px;
    height: 18px;
  }
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
`;

const GeneralError = styled.div`
  background: var(--error-light);
  color: #dc2626;
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 16px;
  border: 1px solid #dc2626;
  font-size: 14px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'secondary' ? 'var(--border-color)' : 'var(--primary-color)'};
  color: ${props => props.variant === 'secondary' ? 'var(--text-primary)' : 'white'};
  border: none;
  padding: 12px 24px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InfoNote = styled.div`
  background: #e3f2fd;
  border-left: 3px solid #2196f3;
  padding: 12px;
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  color: #1565c0;
  margin-bottom: 20px;
`;

const MedicalConditionModal: React.FC<MedicalConditionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  clientId,
  condition
}) => {
  const [loading, setLoading] = useState(false);
  const [diseases, setDiseases] = useState<DiseaseDto[]>([]);
  const [loadingDiseases, setLoadingDiseases] = useState(false);
  const [formData, setFormData] = useState<CreateClientMedicalInfoDto | UpdateClientMedicalInfoDto>({
    diseaseId: 0,
    isPrimary: false,
    diagnosisDate: undefined,
    notes: '',
    physicianName: '',
    physicianPhone: '',
    isTimeSensitive: false,
    wasHospitalizedForThisDisease: false,
    hadSurgeryForThisDisease: false,
    submittedClaimForThisDisease: false,
    ...(condition ? {} : { clientId })
  });
  const [errors, setErrors] = useState<{
    diseaseId?: string;
    diagnosisDate?: string;
    physicianName?: string;
    physicianPhone?: string;
    notes?: string;
    general?: string;
  }>({});

  useEffect(() => {
    if (isOpen) {
      loadDiseases();
      if (condition) {
        setFormData({
          diseaseId: condition.diseaseId,
          isPrimary: condition.isPrimary,
          diagnosisDate: condition.diagnosisDate ? condition.diagnosisDate.split('T')[0] : undefined,
          notes: condition.notes || '',
          physicianName: condition.physicianName || '',
          physicianPhone: condition.physicianPhone || '',
          isTimeSensitive: condition.isTimeSensitive,
          wasHospitalizedForThisDisease: condition.wasHospitalizedForThisDisease || false,
          hadSurgeryForThisDisease: condition.hadSurgeryForThisDisease || false,
          submittedClaimForThisDisease: condition.submittedClaimForThisDisease || false,
          status: condition.status
        });
      } else {
        setFormData({
          clientId,
          diseaseId: 0,
          isPrimary: false,
          diagnosisDate: undefined,
          notes: '',
          physicianName: '',
          physicianPhone: '',
          isTimeSensitive: false,
          wasHospitalizedForThisDisease: false,
          hadSurgeryForThisDisease: false,
          submittedClaimForThisDisease: false,
          status: MedicalConditionStatus.New
        });
      }
      setErrors({});
    }
  }, [isOpen, condition, clientId]);

  const loadDiseases = async () => {
    try {
      setLoadingDiseases(true);
      const data = await diseasesApi.getAll();
      // Filter only active diseases
      setDiseases(data.filter(d => d.isActive));
    } catch (error) {
      console.error('Error loading diseases:', error);
      setErrors({ general: 'שגיאה בטעינת רשימת מחלות' });
    } finally {
      setLoadingDiseases(false);
    }
  };

  // Validate a single field (for onBlur validation)
  const validateField = (field: string, value: any): string => {
    switch (field) {
      case 'diseaseId':
        if (!value || value === 0) {
          return 'יש לבחור מחלה';
        }
        break;

      case 'diagnosisDate':
        if (value) {
          const diagnosisYear = new Date(value).getFullYear();
          const currentYear = new Date().getFullYear();
          if (diagnosisYear > currentYear) {
            return 'שנת אבחון לא יכולה להיות בעתיד';
          }
          if (diagnosisYear < 1900) {
            return 'שנת אבחון לא תקינה';
          }
        }
        break;

      case 'physicianName':
        if (value && value.length > 100) {
          return 'שם הרופא לא יכול להכיל יותר מ-100 תווים';
        }
        break;

      case 'physicianPhone':
        if (value && value.length > 20) {
          return 'טלפון הרופא לא יכול להכיל יותר מ-20 תווים';
        }
        break;

      case 'notes':
        if (value && value.length > 500) {
          return 'הערות לא יכולות להכיל יותר מ-500 תווים';
        }
        break;

      default:
        return '';
    }
    return '';
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    const diseaseIdError = validateField('diseaseId', formData.diseaseId);
    if (diseaseIdError) newErrors.diseaseId = diseaseIdError;

    const diagnosisDateError = validateField('diagnosisDate', formData.diagnosisDate);
    if (diagnosisDateError) newErrors.diagnosisDate = diagnosisDateError;

    const physicianNameError = validateField('physicianName', formData.physicianName);
    if (physicianNameError) newErrors.physicianName = physicianNameError;

    const physicianPhoneError = validateField('physicianPhone', formData.physicianPhone);
    if (physicianPhoneError) newErrors.physicianPhone = physicianPhoneError;

    const notesError = validateField('notes', formData.notes);
    if (notesError) newErrors.notes = notesError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleBlur = (field: string, value: any) => {
    const error = validateField(field, value);
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    } else {
      // Clear error if field is now valid
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as keyof typeof errors];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      if (condition) {
        // Update existing condition
        await medicalConditionsApi.update(condition.id, formData as UpdateClientMedicalInfoDto);
      } else {
        // Create new condition
        await medicalConditionsApi.create(formData as CreateClientMedicalInfoDto);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving medical condition:', error);
      setErrors({ general: handleApiError(error) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={condition ? 'עריכת מחלה' : 'הוספת מחלה'}
      width="600px"
      closable={!loading}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            ביטול
          </Button>
          <Button type="submit" onClick={handleSubmit} disabled={loading || loadingDiseases}>
            {loading ? 'שומר...' : condition ? 'שמירת שינויים' : 'הוספת מחלה'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        {errors.general && (
          <GeneralError>{errors.general}</GeneralError>
        )}

        {!condition && (
          <InfoNote>
            💡 המחלה העיקרית היא הגורם המרכזי לאי-יכולת לעבוד. מחלה משנית היא בעיה בריאותית נוספת שעשויה להשפיע על התיק.
          </InfoNote>
        )}

        <FormGroup>
          <Label>בחר מחלה *</Label>
          <Select
            value={formData.diseaseId}
            onChange={(e) => setFormData({ ...formData, diseaseId: parseInt(e.target.value) })}
            hasError={!!errors.diseaseId}
            disabled={loadingDiseases}
            required
          >
            <option value={0}>-- בחר מחלה --</option>
            {diseases.map(disease => (
              <option key={disease.id} value={disease.id}>
                {disease.name} ({disease.categoryName})
              </option>
            ))}
          </Select>
          {errors.diseaseId && <ErrorMessage>{errors.diseaseId}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>סוג המחלה *</Label>
          <RadioGroup>
            <RadioOption>
              <input
                type="radio"
                name="isPrimary"
                checked={formData.isPrimary === true}
                onChange={() => setFormData({ ...formData, isPrimary: true })}
              />
              מחלה עיקרית
            </RadioOption>
            <RadioOption>
              <input
                type="radio"
                name="isPrimary"
                checked={formData.isPrimary === false}
                onChange={() => setFormData({ ...formData, isPrimary: false })}
              />
              מחלה משנית
            </RadioOption>
          </RadioGroup>
        </FormGroup>

        <FormGroup>
          <Label>סטטוס המחלה</Label>
          <Select
            value={formData.status || MedicalConditionStatus.New}
            onChange={(e) => setFormData({ ...formData, status: parseInt(e.target.value) })}
          >
            <option value={MedicalConditionStatus.New}>חדש</option>
            <option value={MedicalConditionStatus.InTreatment}>בטיפול</option>
            <option value={MedicalConditionStatus.Approved}>אושר</option>
            <option value={MedicalConditionStatus.Rejected}>נדחה</option>
            <option value={MedicalConditionStatus.Pending}>ממתין להחלטה</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>שנת אבחון</Label>
          <Input
            type="date"
            value={formData.diagnosisDate || ''}
            onChange={(e) => setFormData({ ...formData, diagnosisDate: e.target.value || undefined })}
            hasError={!!errors.diagnosisDate}
          />
          {errors.diagnosisDate && <ErrorMessage>{errors.diagnosisDate}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>שם הרופא המטפל</Label>
          <Input
            type="text"
            value={formData.physicianName || ''}
            onChange={(e) => setFormData({ ...formData, physicianName: e.target.value })}
            hasError={!!errors.physicianName}
            placeholder="ד״ר שם משפחה"
            maxLength={100}
          />
          {errors.physicianName && <ErrorMessage>{errors.physicianName}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>טלפון הרופא</Label>
          <Input
            type="tel"
            value={formData.physicianPhone || ''}
            onChange={(e) => setFormData({ ...formData, physicianPhone: e.target.value })}
            hasError={!!errors.physicianPhone}
            placeholder="050-1234567"
            maxLength={20}
          />
          {errors.physicianPhone && <ErrorMessage>{errors.physicianPhone}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>הערות נוספות</Label>
          <TextArea
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            hasError={!!errors.notes}
            placeholder="הערות, פרטים נוספים, תרופות, טיפולים..."
            maxLength={500}
          />
          {errors.notes && <ErrorMessage>{errors.notes}</ErrorMessage>}
          {formData.notes && (
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              {formData.notes.length} / 500 תווים
            </div>
          )}
        </FormGroup>

        <FormGroup>
          <CheckboxGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.isTimeSensitive}
                onChange={(e) => setFormData({ ...formData, isTimeSensitive: e.target.checked })}
              />
              <span>מחלה רגישה לזמן (דורשת טיפול דחוף)</span>
            </CheckboxLabel>
          </CheckboxGroup>
        </FormGroup>

        <FormGroup>
          <Label>שאלות ייעודיות למחלה זו</Label>
          <InfoNote>
            השאלות הבאות מתייחסות ספציפית למחלה זו, ולא לשאלות הכלליות ברמת הלקוח.
          </InfoNote>

          <CheckboxGroup style={{ marginBottom: '12px' }}>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.wasHospitalizedForThisDisease || false}
                onChange={(e) => setFormData({ ...formData, wasHospitalizedForThisDisease: e.target.checked })}
              />
              <span>האם אושפז בגלל מחלה זו?</span>
            </CheckboxLabel>
          </CheckboxGroup>

          <CheckboxGroup style={{ marginBottom: '12px' }}>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.hadSurgeryForThisDisease || false}
                onChange={(e) => setFormData({ ...formData, hadSurgeryForThisDisease: e.target.checked })}
              />
              <span>האם ניתח בגלל מחלה זו?</span>
            </CheckboxLabel>
          </CheckboxGroup>

          <CheckboxGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.submittedClaimForThisDisease || false}
                onChange={(e) => setFormData({ ...formData, submittedClaimForThisDisease: e.target.checked })}
              />
              <span>האם הוגשה תביעה בעבר על מחלה זו?</span>
            </CheckboxLabel>
          </CheckboxGroup>
        </FormGroup>
      </form>
    </Modal>
  );
};

export default MedicalConditionModal;
